import {HttpClient, HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {inject, Injectable, Injector} from "@angular/core";
import {Router} from "@angular/router";
import {LANDING_ROUTE, USER_PROFILE_VIEW_ROUTE} from "src/app/app-routing.module";
import {BehaviorSubject, tap} from "rxjs";
import {LoaderService} from "./loader.service";
import {RaceService} from "./race.service";
import {FacebookLoginProvider, SocialAuthService, SocialUser} from "@abacritt/angularx-social-login";
import {JWT_RESPONSE, LocalStorageService} from "./local-storage.service";
import {ActionService} from "./action.service";
import {UserDetailService} from "./user-detail.service";
import {PermissionService} from "./permission.service";
import {
  AccountRecoveryUser,
  ActionResult, ADMINISTRATOR_ROLE, AuthServerType, ExternalLoginRequest,
  JwtResponse,
  LoginRequest, ORGANIZER_ROLE,
  RegisterUser,
  TokenRefreshRequest,
  TokenRefreshResponse, User, UserDetail
} from "@model";
import {EnvironmentService} from "./environment.service";

export const LOGIN_URL: string = "/api/auth/login";
export const EXTERNAL_LOGIN_URL: string = "/api/auth/externallogin";
export const REFRESH_JWT_TOKEN_URL: string = "/api/auth/refreshtoken";

@Injectable({ providedIn:'root' })
export class AuthenticationService {

  injector: Injector = inject(Injector);
  static readonly NEW_USER_VERIFICATION_FAILED: number = 11;
  static readonly USER_ALREADY_REGISTERED: number = 12;
  static readonly TOKEN_NOT_VALID: number = 13;
  static readonly PASSWORDS_DO_NOT_MATCH: number = 14;

  private static _jwtResponse: BehaviorSubject<JwtResponse | null> = new BehaviorSubject<JwtResponse | null>(null);
  private static _userRole: BehaviorSubject<String | null> = new BehaviorSubject<String | null>(null);

  get jwtResponse(): BehaviorSubject<JwtResponse | null> {
    return AuthenticationService._jwtResponse;
  }

  get userRole(): BehaviorSubject<String | null> {
    return AuthenticationService._userRole;
  }

  constructor(private http: HttpClient, private router: Router, private loaderService: LoaderService,
              private socialAuthService: SocialAuthService, private actionService: ActionService,
              private localStorageService: LocalStorageService, private permissionSerivce: PermissionService,
              private environmentService:EnvironmentService) {
    this.socialLoginAuthState();
    this.subscribeToJwtChange();

  }
  isAuthenticated(): boolean {
    return !!this.jwtResponse.getValue()?.token;
  }

  isAdmin(): boolean {
    return this.userRole.value === ADMINISTRATOR_ROLE;
  }

  isOrganiser(): boolean {
    return this.userRole.value === ORGANIZER_ROLE;
  }

  subscribeToJwtChange() {
    this.jwtResponse.subscribe(val => {
      if (val) {
        if (val.roles) {
          val.roles.forEach((role: string) => {
            this.userRole.next(role);
            this.permissionSerivce.initPermissions(role)
          });
        } else {
          this.userRole.next(null);
          this.permissionSerivce.emptyPermissions();
        }
      } else {
        this.userRole.next(null);
        this.permissionSerivce.emptyPermissions();
      }
    });
  }

  login(loginRequest: LoginRequest, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<JwtResponse>(this.environmentService.getApiUrl() + LOGIN_URL, loginRequest)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((jwtResponse: JwtResponse) => {
        if (jwtResponse.token) {
          this.jwtResponse.next(jwtResponse);
          this.localStorageService.saveData(JWT_RESPONSE, JSON.stringify(jwtResponse));
          this.injector.get(UserDetailService).fetchUserProfile();
          if (this.actionService.getActions().length > 0) {
            this.actionService.dispatchActions();
          } else {
            this.router.navigate([USER_PROFILE_VIEW_ROUTE]);
          }

        } else {
          this.localStorageService.removeData(JWT_RESPONSE);
          this.permissionSerivce.emptyPermissions();
          this.jwtResponse.next(null);
        }
      }, (error: any) => {
        this.localStorageService.removeData(JWT_RESPONSE);
        this.permissionSerivce.emptyPermissions();
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
          onError();
        }
      });
  }

  refreshJwtToken(jwtResponse: JwtResponse, tokenRefreshRequest: TokenRefreshRequest) {
    return this.http.post<TokenRefreshResponse>(this.environmentService.getApiUrl() + REFRESH_JWT_TOKEN_URL, tokenRefreshRequest)
      .pipe((
        tap((result: TokenRefreshResponse) => {
          jwtResponse.refreshToken = result.refreshToken;
          jwtResponse.token = result.accessToken;
          this.jwtResponse.next(jwtResponse);
          this.localStorageService.saveData(JWT_RESPONSE, JSON.stringify(jwtResponse));
        }, (_error: any) => {
          this.localStorageService.removeData(JWT_RESPONSE);
          this.permissionSerivce.emptyPermissions();
        })));
  }

  logout() {
    this.loaderService.dismissLoading();
    this.injector.get(RaceService).emptyRaces();
    this.injector.get(UserDetailService).emptyCurrentUserDetails();
    this.jwtResponse.next(null);
    this.localStorageService.removeData(JWT_RESPONSE);
    this.router.navigate([LANDING_ROUTE]);
    this.socialAuthService.signOut();
  }

  signUp(registerUser: RegisterUser, onSuccess: () => void, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<UserDetail>>(this.environmentService.getApiUrl() + "/api/auth/register", registerUser)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((actionResult: ActionResult<UserDetail>) => {
        if (actionResult.resultCode === AuthenticationService.USER_ALREADY_REGISTERED) {
          onError();
        } else {
          onSuccess();
        }
      });
  }

  verification(accountRecoveryUser: AccountRecoveryUser, onSuccess: () => void, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/auth/verification", accountRecoveryUser)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode === AuthenticationService.USER_ALREADY_REGISTERED) {
          onError();
        } else {
          onSuccess();
        }
      });
  }

  accountRecovery(accountRecoveryUser: AccountRecoveryUser, onSuccess: () => void, onError: (value: number) => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/auth/accountrecovery", accountRecoveryUser)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode === AuthenticationService.TOKEN_NOT_VALID) {
          onError(AuthenticationService.TOKEN_NOT_VALID);
        } else if (actionResult.resultCode === AuthenticationService.PASSWORDS_DO_NOT_MATCH) {
          onError(AuthenticationService.PASSWORDS_DO_NOT_MATCH);
        } else {
          onSuccess();
        }
      });
  }

  recoverAccount(user: User, onSuccess: () => void, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/auth/accountrecoveryrequest", user)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          onSuccess();
        } else {
          onError();
        }
      });
  }

  socialLoginAuthState() {
    this.socialAuthService.authState.subscribe((socialUser: SocialUser) => {
      if (socialUser) {
        this.externalLogin(new ExternalLoginRequest(socialUser.provider === 'GOOGLE' ?
          AuthServerType.GOOGLE : AuthServerType.FACEBOOK, socialUser.provider === 'GOOGLE' ? socialUser.idToken : socialUser.authToken))
      }
    });
  }

  facebookSignIn() {
    this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID).then((socialUser: SocialUser) => {
      if (socialUser) {
        this.externalLogin(new ExternalLoginRequest(AuthServerType.FACEBOOK, socialUser.authToken))
      }
    });
  }

  externalLogin(externalLoginRequest: ExternalLoginRequest) {
    this.loaderService.startLoading();
    this.http.post<JwtResponse>(this.environmentService.getApiUrl() + EXTERNAL_LOGIN_URL, externalLoginRequest)
      .pipe((
        tap(() => this.loaderService.dismissLoading())))
      .subscribe((jwtResponse: JwtResponse) => {
        if (jwtResponse.token) {
          this.jwtResponse.next(jwtResponse);
          this.localStorageService.saveData(JWT_RESPONSE, JSON.stringify(jwtResponse));
          this.injector.get(UserDetailService).fetchUserProfile();

          if (this.actionService.getActions().length > 0) {
            this.actionService.dispatchActions();
          } else {
            this.router.navigate([USER_PROFILE_VIEW_ROUTE]);
          }
        } else {
          this.localStorageService.removeData(JWT_RESPONSE);
          this.jwtResponse.next(null);
        }
      }, (error: any) => {
        this.localStorageService.removeData(JWT_RESPONSE);
        this.permissionSerivce.emptyPermissions();
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {

        }
      });
  }
}
